import { loadScript } from '@paypal/paypal-js'
import api from './api'

class PayPalService {
  constructor() {
    this.paypal = null
    this.clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID || 'test'
  }

  async loadPayPal() {
    if (this.paypal) {
      return this.paypal
    }

    try {
      this.paypal = await loadScript({
        'client-id': this.clientId,
        currency: 'USD',
        intent: 'capture'
      })
      return this.paypal
    } catch (error) {
      console.error('Failed to load PayPal SDK:', error)
      throw error
    }
  }

  async createSubscription(plan, billingCycle = 'monthly') {
    try {
      const response = await api.post('/payment/subscription', {
        plan: plan,
        payment_method: 'paypal',
        billing_cycle: billingCycle
      })

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create subscription')
      }

      return response.data
    } catch (error) {
      console.error('Failed to create PayPal subscription:', error)
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  }

  async getSubscriptionStatus() {
    try {
      const response = await api.get('/payment/subscription/status')
      return response.data
    } catch (error) {
      console.error('Failed to get subscription status:', error)
      throw error
    }
  }

  async cancelSubscription() {
    try {
      const response = await api.delete('/payment/subscription')
      return response.data
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
      throw error
    }
  }

  async getPaymentHistory() {
    try {
      const response = await api.get('/payment/history')
      return response.data
    } catch (error) {
      console.error('Failed to get payment history:', error)
      throw error
    }
  }

  async getPendingTransaction() {
    try {
      const response = await api.get('/payment/pending-transaction')
      return response.data
    } catch (error) {
      console.error('Failed to get pending transaction:', error)
      throw error
    }
  }

  async renderPayPalButtons(container, options) {
    try {
      // Create subscription and get approval URL
      const subscriptionData = await this.createSubscription(
        options.plan || 'pro',
        options.billingCycle || 'monthly'
      )

      if (subscriptionData.approval_url) {
        // Redirect to PayPal approval page
        window.location.href = subscriptionData.approval_url
      } else {
        throw new Error('No approval URL received from server')
      }
    } catch (error) {
      console.error('Error creating subscription:', error)
      if (options.onError) {
        options.onError(error)
      }
      throw error
    }
  }

  getPlanInfo(planType) {
    const plans = {
      pro: {
        name: 'Pro Plan',
        monthly: '$24',
        annual: '$228',
        period: 'month',
        features: [
          'Up to 5 businesses',
          'Custom widget design',
          'Advanced analytics',
          'API access',
          'Priority support'
        ]
      },
      enterprise: {
        name: 'Enterprise Plan',
        monthly: '$99',
        annual: '$948',
        period: 'month',
        features: [
          'Unlimited businesses',
          'White-label solution',
          'Advanced integrations',
          'Custom development',
          'Dedicated support',
          'SLA guarantee'
        ]
      },
      test: {
        name: 'Test Plan',
        price: '$15',
        period: 'day',
        features: [
          'Test subscription',
          'Limited features',
          '1 day duration'
        ]
      }
    }

    return plans[planType] || null
  }
}

export default new PayPalService()